import { useState, useMemo, useContext, useEffect } from 'react';
import { z } from 'zod';
import { displayMoneyFormat } from '../../../util/util';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { keyBy } from 'lodash';
import { useAutoSaveConfigs } from '../../../hooks/use-auto-save-configs';
import { useZodValidation } from '../../../hooks/use-zod-validation';
import { TenantContext } from '../../../context/tenant-context';

// Return type for the useConfigCreditCardPassThrough hook
interface UseConfigCreditCardPassThroughReturn {
  // Values
  isLoading: boolean;
  isSaving: boolean;
  isBankACHEnabled: boolean;
  isCardFeeEnabled: boolean;
  isBankFeeEnabled: boolean;

  useCustomDisclaimer: boolean;
  customDisclaimer: string;

  defaultPassthroughPercentage: number;
  customPercentageFee: number;
  customFixedFee: number;

  showDisclaimerSection: boolean;

  feeExceptionPerCustomer: FeeExceptionPerCustomerOption;

  // Preview values
  previewFee: string;
  previewDisclaimer: string;

  // Validation
  errors: {
    customPercentageFee?: string;
    customFixedFee?: string;
    customDisclaimer?: string;
  };
  isSummaryValid: boolean;

  // Setters
  setIsBankACHEnabled: (value: boolean) => void;
  setIsCardFeeEnabled: (value: boolean) => void;
  setIsBankFeeEnabled: (value: boolean) => void;
  setUseCustomDisclaimer: (value: boolean) => void;
  setCustomDisclaimer: (value: string) => void;
  setCustomPercentageFee: (value: number) => void;
  setCustomFixedFee: (value: number) => void;
  setFeeExceptionPerCustomer: (value: FeeExceptionPerCustomerOption) => void;
}

// Option for the fee exception per customer
interface FeeExceptionPerCustomerOption {
  label: string;
  value: string;
}

// Options for the fee exception per customer
export const FEE_EXCEPTIONS_PER_CUSTOMER_OPTIONS: FeeExceptionPerCustomerOption[] = [
  { label: 'Apply the fee for all customers', value: 'apply_fee' },
  { label: 'Apply the fee unless specified for a customer', value: 'optionally_apply_fee' },
  { label: 'Waive the fee unless specified for a customer', value: 'optionally_waive_fee' },
];

// Replace placeholders in the disclaimer text
const replacePlaceholders = (text: string, percentage: string, fixed: string): string => {
  return text.replace(/\{percentage\}/g, `${percentage}%`).replace(/\{fixed\}/g, `$${fixed}`);
};

// Validation schema for the credit card pass-through configuration
const creditCardPassThroughSchema = z.object({
  customPercentageFee: z
    .number({ message: 'Percentage fee must be a number' })
    .min(0, 'Percentage fee cannot be negative')
    .max(100, 'Percentage fee cannot exceed 100%'),
  customFixedFee: z.number({ message: 'Fixed fee must be a number' }).min(0, 'Fixed fee cannot be negative'),
  customDisclaimer: z.string().max(200, 'Disclaimer cannot exceed 200 characters').optional(),
});

// Hook for the credit card pass-through configuration
export const useConfigCreditCardPassThrough = (): UseConfigCreditCardPassThroughReturn => {
  const { configuration } = useContext(TenantContext);
  const { getMultipleConfigurations } = useAPI();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState<boolean>(false);

  // Bank/ACH
  const [isBankACHEnabled, setIsBankACHEnabled] = useState<boolean>(false);
  const [isBankFeeEnabled, setIsBankFeeEnabled] = useState<boolean>(false);

  // Card
  const [isCardFeeEnabled, setIsCardFeeEnabled] = useState<boolean>(false);
  const [customPercentageFee, setCustomPercentageFee] = useState<number>(0);
  const [customFixedFee, setCustomFixedFee] = useState<number>(0);
  const [feeExceptionPerCustomer, setFeeExceptionPerCustomer] = useState<FeeExceptionPerCustomerOption>(
    FEE_EXCEPTIONS_PER_CUSTOMER_OPTIONS[0],
  );
  const [platformFeePercentage, setPlatformFeePercentage] = useState<number>(0);
  const [customPlatformFeePercentage, setCustomPlatformFeePercentage] = useState<number>(0);

  // Safe setter for the custom percentage fee
  const handleSetCustomPercentageFee = (value: number) => {
    const validValue = isNaN(value) ? 0 : value;
    setCustomPercentageFee(validValue);
  };

  // Safe setter for the custom fixed fee
  const handleSetCustomFixedFee = (value: number) => {
    const validValue = isNaN(value) ? 0 : value;
    setCustomFixedFee(validValue);
  };

  // Disclaimer
  const [useCustomDisclaimer, setUseCustomDisclaimer] = useState<boolean>(false);
  const [customDisclaimer, setCustomDisclaimer] = useState<string>('');

  const showDisclaimerSection = isCardFeeEnabled || customPercentageFee > 0 || customFixedFee > 0;
  const currency = configuration?.currency || 'USD';

  // Load configurations
  useEffect(() => {
    getMultipleConfigurations([
      'CONF_ENABLE_ACH',
      'CONF_ACH_PASSTHROUGH',
      'CONF_CHARGE_PASSTHROUGH',
      'CONF_CUSTOMER_CC_FEE_PERCENT',
      'CONF_CUSTOMER_CC_FEE_FIXED',
      'CONF_USE_FEE_DISCLAIMER_OVERRIDE',
      'CONF_FEE_DISCLAIMER_OVERRIDE',
      'CONF_PROCESSING_FEE_OVERRIDE',
      // Configs to show the default passthrough percentage
      'CONF_PLATFORM_FEE_PERCT',
      'CONF_CUSTOM_PLATFORM_FEE_PERCENT',
    ])
      .then((response) => {
        const conf = keyBy(response, (c) => c.variable);
        setIsBankACHEnabled(conf['CONF_ENABLE_ACH']?.value === '1');
        setIsBankFeeEnabled(conf['CONF_ACH_PASSTHROUGH']?.value === '1');
        setIsCardFeeEnabled(conf['CONF_CHARGE_PASSTHROUGH']?.value === '1');
        setCustomPercentageFee(parseFloat(conf['CONF_CUSTOMER_CC_FEE_PERCENT']?.value || '0'));
        setCustomFixedFee(parseFloat(conf['CONF_CUSTOMER_CC_FEE_FIXED']?.value || '0'));
        setUseCustomDisclaimer(conf['CONF_USE_FEE_DISCLAIMER_OVERRIDE']?.value === '1');
        setCustomDisclaimer(conf['CONF_FEE_DISCLAIMER_OVERRIDE']?.value || '');
        setFeeExceptionPerCustomer(
          FEE_EXCEPTIONS_PER_CUSTOMER_OPTIONS.find((o) => o.value === conf['CONF_PROCESSING_FEE_OVERRIDE']?.value) ||
            FEE_EXCEPTIONS_PER_CUSTOMER_OPTIONS[0],
        );

        // Set the default passthrough percentage
        const platformPct = parseFloat(conf['CONF_PLATFORM_FEE_PERCT']?.value || '0');
        const customPct = parseFloat(conf['CONF_CUSTOM_PLATFORM_FEE_PERCENT']?.value || '0');
        setPlatformFeePercentage(Number.isNaN(platformPct) ? 0 : platformPct);
        setCustomPlatformFeePercentage(Number.isNaN(customPct) ? 0 : customPct);
      })
      .catch((err) => {
        console.error('Failed to load configurations:', err);
      })
      .finally(() => {
        setIsLoading(false);
        // Mark initial load as complete to enable auto-save
        setIsInitialLoadComplete(true);
      });
  }, []);

  // Validation
  const { isValid: isSummaryValid, errors: validationErrors } = useZodValidation(creditCardPassThroughSchema, {
    customPercentageFee,
    customFixedFee,
    customDisclaimer,
  });

  // Auto-save all configuration changes when values change
  const { isSaving } = useAutoSaveConfigs(
    {
      CONF_ENABLE_ACH: isBankACHEnabled ? '1' : '0',
      CONF_ACH_PASSTHROUGH: isBankFeeEnabled ? '1' : '0',
      CONF_CHARGE_PASSTHROUGH: isCardFeeEnabled ? '1' : '0',
      CONF_CUSTOMER_CC_FEE_PERCENT: customPercentageFee.toString(),
      CONF_CUSTOMER_CC_FEE_FIXED: customFixedFee.toString(),
      CONF_PROCESSING_FEE_OVERRIDE: feeExceptionPerCustomer.value,
      CONF_USE_FEE_DISCLAIMER_OVERRIDE: useCustomDisclaimer ? '1' : '0',
      CONF_FEE_DISCLAIMER_OVERRIDE: customDisclaimer,
    },
    {
      enabled: isInitialLoadComplete,
      // Per-field validation - only block fields that have errors
      isValid: {
        CONF_CUSTOMER_CC_FEE_PERCENT: !validationErrors.customPercentageFee,
        CONF_CUSTOMER_CC_FEE_FIXED: !validationErrors.customFixedFee,
        CONF_FEE_DISCLAIMER_OVERRIDE: !validationErrors.customDisclaimer,
        // Other fields (ACH, passthrough, etc.) will save even if fees are invalid
      },
      delay: 800,
      setters: {
        CONF_ENABLE_ACH: (value) => setIsBankACHEnabled(value === '1'),
        CONF_ACH_PASSTHROUGH: (value) => setIsBankFeeEnabled(value === '1'),
        CONF_CHARGE_PASSTHROUGH: (value) => setIsCardFeeEnabled(value === '1'),
        CONF_CUSTOMER_CC_FEE_PERCENT: (value) => handleSetCustomPercentageFee(parseFloat(value)),
        CONF_CUSTOMER_CC_FEE_FIXED: (value) => handleSetCustomFixedFee(parseFloat(value)),
        CONF_PROCESSING_FEE_OVERRIDE: (value) => {
          const option = FEE_EXCEPTIONS_PER_CUSTOMER_OPTIONS.find((o) => o.value === value);
          if (option) setFeeExceptionPerCustomer(option);
        },
        CONF_USE_FEE_DISCLAIMER_OVERRIDE: (value) => setUseCustomDisclaimer(value === '1'),
        CONF_FEE_DISCLAIMER_OVERRIDE: (value) => setCustomDisclaimer(value),
      },
      showSuccessToast: true,
      showErrorToast: true,
    },
  );

  // Calculate preview fee and disclaimer based on scenarios
  const { previewFee, previewDisclaimer } = useMemo(() => {
    if (!isSummaryValid) return { previewFee: 'Error', previewDisclaimer: '(there is an error in the configuration)' };

    const noFeesDisclaimer = '(no disclaimer will appear on invoices or contracts)';

    // Calculate base fees
    const stripePercentage = isCardFeeEnabled ? platformFeePercentage + customPlatformFeePercentage : 0;
    const stripeFixed = isCardFeeEnabled ? 0.3 : 0;

    const totalPercentage = stripePercentage + customPercentageFee;
    const totalFixed = stripeFixed + customFixedFee;

    // Determine if any fees are configured
    const hasAnyFees = totalPercentage > 0 || totalFixed > 0;

    if (!hasAnyFees) {
      return { previewFee: 'None', previewDisclaimer: noFeesDisclaimer };
    }

    // Build fee string
    const feeParts = [];
    if (totalPercentage > 0) {
      feeParts.push(`${totalPercentage.toFixed(1)}%`);
    }
    if (totalFixed > 0) {
      feeParts.push(`${displayMoneyFormat(totalFixed, currency)}`);
    }
    const fee = feeParts.join(' + ');

    // Build default disclaimer
    let disclaimer = `An additional fee of ${fee} may apply to credit card transactions.`;

    // Handle custom disclaimer text
    if (useCustomDisclaimer) {
      if (customDisclaimer.trim() === '') {
        disclaimer = noFeesDisclaimer;
      } else {
        // Extract values from calculated fee for placeholder replacement
        const percentageValue = totalPercentage > 0 ? totalPercentage.toFixed(1) : '0';
        const fixedValue = totalFixed > 0 ? displayMoneyFormat(totalFixed, currency) : '0';
        disclaimer = replacePlaceholders(customDisclaimer, percentageValue, fixedValue);
      }
    }

    return { previewFee: fee, previewDisclaimer: disclaimer };
  }, [isCardFeeEnabled, customPercentageFee, customFixedFee, useCustomDisclaimer, customDisclaimer, isSummaryValid]);

  return {
    // Values
    isLoading,
    isSaving,
    isBankACHEnabled,
    isCardFeeEnabled,
    isBankFeeEnabled,
    useCustomDisclaimer,
    customDisclaimer,
    customPercentageFee,
    customFixedFee,
    showDisclaimerSection,
    feeExceptionPerCustomer,
    defaultPassthroughPercentage: platformFeePercentage + customPlatformFeePercentage,

    // Preview values
    previewFee,
    previewDisclaimer,

    // Validation
    errors: validationErrors,
    isSummaryValid,

    // Setters
    setIsBankACHEnabled,
    setIsCardFeeEnabled,
    setIsBankFeeEnabled,
    setUseCustomDisclaimer,
    setCustomDisclaimer,
    setCustomPercentageFee: handleSetCustomPercentageFee,
    setCustomFixedFee: handleSetCustomFixedFee,
    setFeeExceptionPerCustomer,
  };
};
